import { isNil } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  useAreaTitle,
  useRideEmptyStationsAllowed,
  useRideStationContext,
} from "@ryde/redux";
import {
  calculatePassengersInfoSeatsCount,
  combineStrings,
  computeRideStationType,
  getRealStationListOrdinal,
  getRideStationDuration,
  getWaypointCategoryVisual,
  INITIAL_RIDE_STATIONS_COLLAPSED,
  isCollapsedRideStationSF,
  isRideGreyed,
  isStationRouteWaypointStation,
  NO_VALUE_DISPLAY,
  rideSelectedPassengersIdsAF,
  RideStationType,
  unique,
  useIsMobile,
  useIsShuttleRideWithManualCount,
  useSystemParams,
} from "@ryde/shared";

import { resolveRideStationDefaultSupplierPin } from "./ride-station-default-supplier-pin.utils";

export const useRideStationRow = () => {
  const {
    index = 0,
    mode,
    station,
    rideDto,
    stationPassengers = [],
    isStationDraggable,
    attributes,
    listeners,
    isFirstStation,
    isLastStation,
  } = useRideStationContext();

  const {
    id: stationId = 0,
    name: stationName = "",
    areaId,
    arrivalTime,
  } = station || {};
  const { id: rideId = 0 } = rideDto || {};

  const { isMobile } = useIsMobile();
  const { t } = useTranslation();
  const isGreyed = isRideGreyed(rideDto);

  const [isCollapsed, setIsCollapsed] = useRecoilState(
    isCollapsedRideStationSF([rideId, stationId]),
  );
  const areaTitleDto = useAreaTitle(areaId);
  const {
    title: areaTitle,
    areaGroupName,
    defaultSupplierId: areaDefaultSupplierId,
  } = areaTitleDto;

  const { skipSettingSupplierByAreaGroup } = useRideEmptyStationsAllowed(
    mode,
    rideDto,
  );

  const supplierIdFromAreaGroupOnRide =
    rideDto?.metadata?.supplierIdFromAreaGroup;
  const { useDestinationAreaForDirectionBAreaGroupDefault } = useSystemParams();

  const {
    showDefaultSupplierPin,
    greyDefaultAreaMarkerForSkipWithoutPriorityId,
    isSupplierLookupStation,
  } = resolveRideStationDefaultSupplierPin({
    areaDefaultSupplierId,
    skipSettingSupplierByAreaGroup,
    supplierIdFromAreaGroupOnRide,
    direction: rideDto?.direction,
    isFirstStation,
    isLastStation,
    useDestinationAreaForDirectionB:
      useDestinationAreaForDirectionBAreaGroupDefault,
  });

  const [selectedPassIds, setSelectedPassIds] = useRecoilState(
    rideSelectedPassengersIdsAF(rideDto?.id || 0),
  );

  // shuttle rides with the manual-count feature show the driver's counted boarding
  // passengers per station (station.seatsCountByDriver) and only the boarding (blue "In") icon.
  const isShuttleManualCount = useIsShuttleRideWithManualCount(rideDto);
  const manualBoardedCount = station?.seatsCountByDriver ?? 0;
  const manualStatusType =
    manualBoardedCount > 0 ? RideStationType.In : RideStationType.None;

  const statusType = isShuttleManualCount
    ? manualStatusType
    : computeRideStationType(stationPassengers, stationId);

  const stationFullName = combineStrings(
    stationName,
    station?.locationInfo?.fullAddress,
    " (",
    ")",
  );

  const allPassengersIds = stationPassengers.map(x => x.id);

  const stationPassengersSeats = isShuttleManualCount
    ? manualBoardedCount
    : calculatePassengersInfoSeatsCount(stationPassengers);

  const allPassengersSelected =
    allPassengersIds.length > 0 &&
    allPassengersIds.every(p => selectedPassIds.some(s => s === p));

  const duration = (() => {
    const durationMin = getRideStationDuration(rideDto, index);

    if (isNil(durationMin) || Number.isNaN(durationMin)) {
      return NO_VALUE_DISPLAY;
    }

    return `${durationMin} ${t("general.MIN")}`;
  })();

  const toggleCollapsed = useEvent(() => {
    setIsCollapsed(collapsed => !collapsed);
  });

  const checkStationPassengers = useEvent(() => {
    if (allPassengersSelected) {
      const allPassengersIdsSet = new Set(allPassengersIds);
      setSelectedPassIds(ids => ids.filter(p => !allPassengersIdsSet.has(p)));
    } else {
      setSelectedPassIds(ids => unique([...ids, ...allPassengersIds]));
    }
  });

  useEffect(() => {
    setIsCollapsed(INITIAL_RIDE_STATIONS_COLLAPSED);
  }, [setIsCollapsed]);

  const isWaypoint = isStationRouteWaypointStation(station);
  const waypointVisual = getWaypointCategoryVisual(station?.waypointCategory);
  const realStationOrdinal = getRealStationListOrdinal({
    station,
    stations: rideDto?.metadata?.stationsInfo,
    listIndex: index,
  });

  return {
    index,
    mode,
    isMobile,
    isGreyed,
    isCollapsed,
    toggleCollapsed,
    stationName,
    stationFullName,
    stationPassengersSeats,
    allPassengersSelected,
    checkStationPassengers,
    duration,
    areaTitle,
    areaGroupName,
    showDefaultSupplierPin,
    greyDefaultAreaMarkerForSkipWithoutPriorityId,
    isSupplierLookupStation,
    station,
    areaId,
    stationId,
    rideId,
    rideDto,
    arrivalTime,
    statusType,
    isStationDraggable,
    attributes,
    listeners,
    isWaypoint,
    waypointVisual,
    realStationOrdinal,
  };
};
